import type { IEntity } from "./types";

import { ApolloProvider } from "@apollo/client";
import React, { useEffect, useState } from "react";
import {
  RouterProvider,
  createBrowserRouter,
  Navigate,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";

import { apolloClient } from "./apollo/apolloClient";
import Layout from "./components/layout";
import SuspenseWrapper from "./components/ui/suspenseWrapper";
import WithEventEditAccess from "./components/wrapper/WithEventEditAccess";
import { DEFAULT_ENTITY } from "./constants";
import { createNewEventSignal } from "./graphql/globalVar/state";
import { base64ToFile, imageFileToBase64 } from "./helpers";
import {
  FirstStepLazy,
  SecondStepLazy,
  ThirdStepLazy,
  FourthStepLazy,
  ProjectSettingsLazy,
  CustomDomainLazy,
  AddDomainSubdomainLazy,
  MediaDesignLazy,
  PromoCodesPageLazy,
  TicketScannerPageLazy,
  ProfilePageLazy,
  PaymentPageLazy,
  StatisticsPageLazy,
  PromotionPageLazy,
  TicketScannerStepLazy,
  TicketDesignStepLazy,
  TicketBannerStepLazy,
  TicketShareStepLazy,
  TicketQrCodeStepLazy,
  MyEventsPageLazy,
  EventPageLazy,
  ScansTablePageLazy,
  ArchivePageLazy,
  PaymentTablePageLazy,
  AttendeesTablePageLazy,
  AccessPageLazy,
  CheckpointAccessHistoryPageLazy,
  TicketOfficePageLazy,
  PreOrderPageLazy,
} from "./pages";

const pathArray = [
  "/create-event",
  "/tickets-info",
  "/payment-info",
  "/check-publish",
];

const EVENT_ID_EDIT_PATH_REGEX = /\/(\d+)\/edit/;

export enum WhiteLabelPaths {
  ProjectSettings = "/project-settings",
  CustomDomain = "/custom-domain",
  MediaDesign = "/media-design",
}

export enum MenuPaths {
  PromoCodes = "/promo-codes",
}

export default function App() {
  const [submittedStep, setSubmittedStep] = useState(0);
  const [entity, setEntity] = useState<IEntity>(DEFAULT_ENTITY);
  const handleUpdateEntity = async (values: IEntity, currentStep: number) => {
    setEntity(values);
    setSubmittedStep(currentStep);

    localStorage.setItem(
      "entity",
      JSON.stringify({
        ...entity,
        ...values,
        galery: null,
        banner: null,
        poster: null,
      })
    );
    localStorage.setItem(
      "options",
      JSON.stringify({ submittedStep: currentStep })
    );
    if (values.poster !== null) {
      const posterBase64 = await imageFileToBase64(values.poster);
      localStorage.setItem("poster", posterBase64);
    }
  };

  useEffect(() => {
    function handleNewEvent() {
      setEntity(DEFAULT_ENTITY);
      setSubmittedStep(0);
      createNewEventSignal.onNextChange(handleNewEvent);
    }
    return createNewEventSignal.onNextChange(handleNewEvent);
  }, []);

  useEffect(() => {
    const entityData = localStorage.getItem("entity");
    const posterData = localStorage.getItem("poster");
    const optionsData = localStorage.getItem("options");

    if (entityData) {
      const parsedEntityData = JSON.parse(entityData) as IEntity;
      setEntity((prev) => ({ ...prev, ...parsedEntityData }));
    }
    if (posterData) {
      const posterFile = base64ToFile(posterData, "poster");
      setEntity((prev) => ({ ...prev, poster: posterFile }));
    }
    if (optionsData) {
      const { submittedStep } = JSON.parse(optionsData) as {
        submittedStep: number;
      };
      setSubmittedStep(submittedStep);
    }
  }, []);

  useEffect(() => {
    const checkStep = () => {
      const currentPath = window.location.pathname;
      if (!EVENT_ID_EDIT_PATH_REGEX.test(currentPath)) return;
      if (!pathArray.includes(currentPath)) return;

      const options = localStorage.getItem("options");

      if (options) {
        const { submittedStep } = JSON.parse(options);
        const newPath = pathArray[submittedStep];
        if (currentPath !== newPath) {
          window.location.href = newPath;
        }
      } else {
        const defaultPath = pathArray[0];
        if (currentPath !== defaultPath) {
          window.location.href = defaultPath;
        }
      }
    };

    setTimeout(() => {
      checkStep();
    }, 0);
  }, []);

  const stepProps = {
    entity,
    handleUpdateEntity,
    step: submittedStep + 1,
  };

  const routes = (locale: string | undefined = "") => [
    {
      path: `/${locale}`,
      element: <Layout />,
      children: [
        {
          index: true,
          element: <Navigate to="create-event" replace />,
        },
        {
          element: <SuspenseWrapper />,
          children: [
            {
              path: "create-event",
              element: <FirstStepLazy {...stepProps} />,
            },
            {
              path: "create-event/:id/edit",
              element: (
                <WithEventEditAccess>
                  <FirstStepLazy {...stepProps} />
                </WithEventEditAccess>
              ),
            },
            {
              path: "events",
              element: <PromotionPageLazy />,
              children: [
                {
                  path: ":id/scanner-promotion",
                  element: <TicketScannerStepLazy />,
                },
                {
                  path: ":id/tickets-design",
                  element: <TicketDesignStepLazy />,
                },
                {
                  path: ":id/add-banner",
                  element: <TicketBannerStepLazy />,
                },
                {
                  path: ":id/share",
                  element: <TicketShareStepLazy />,
                },
                {
                  path: ":id/step-qr-event",
                  element: <TicketQrCodeStepLazy />,
                },
              ],
            },
            {
              path: "tickets-info",
              element: <SecondStepLazy {...stepProps} />,
            },
            {
              path: "tickets-info/:id/edit",
              element: (
                <WithEventEditAccess>
                  <SecondStepLazy {...stepProps} />
                </WithEventEditAccess>
              ),
            },
            {
              path: "payment-info",
              children: [
                {
                  path: ":id/edit",
                  element: (
                    <WithEventEditAccess>
                      <ThirdStepLazy {...stepProps} />
                    </WithEventEditAccess>
                  ),
                },
                {
                  path: "connect/:eventId",
                  element: (
                    <WithEventEditAccess>
                      <ThirdStepLazy {...stepProps} />
                    </WithEventEditAccess>
                  ),
                },
              ],
            },
            {
              path: "check-publish",
              element: (
                <WithEventEditAccess eventId={entity.id}>
                  <FourthStepLazy {...stepProps} />
                </WithEventEditAccess>
              ),
            },
            {
              path: "project-settings",
              element: <ProjectSettingsLazy />,
            },
            {
              path: "custom-domain",
              element: <CustomDomainLazy />,
            },
            {
              path: "custom-domain/add-domain",
              element: <AddDomainSubdomainLazy />,
            },
            {
              path: "custom-domain/add-subdomain",
              element: <AddDomainSubdomainLazy />,
            },
            {
              path: "media-design",
              element: <MediaDesignLazy />,
            },
            {
              path: "promo-codes",
              element: <PromoCodesPageLazy />,
            },
            {
              path: "ticket-scanner",
              element: <TicketScannerPageLazy />,
            },
            {
              path: "profile",
              element: <ProfilePageLazy />,
            },
            {
              path: "payment",
              element: <PaymentPageLazy />,
            },
            {
              path: "statistics",
              element: <StatisticsPageLazy />,
            },
            {
              path: "my-events",
              element: <MyEventsPageLazy />,
            },
            {
              path: "dashboard/:eventId",
              children: [
                {
                  index: true,
                  element: <EventPageLazy />,
                },
                {
                  path: "scans",
                  element: <ScansTablePageLazy />,
                },
                {
                  path: "payment-history",
                  element: <PaymentTablePageLazy />,
                },
                {
                  path: "attendees",
                  element: <AttendeesTablePageLazy />,
                },
                {
                  path: "checkpoint-access",
                  element: <AccessPageLazy />,
                },
                {
                  path: "checkpoint-access/history",
                  element: <CheckpointAccessHistoryPageLazy />,
                },
                {
                  path: "ticket-office",
                  element: <TicketOfficePageLazy />,
                },
                {
                  path: "ticket-office/event/pre-order",
                  element: <PreOrderPageLazy />,
                },
              ],
            },
            {
              path: "archive-events",
              element: <ArchivePageLazy />,
            },
          ],
        },
      ],
    },
  ];

  const router = createBrowserRouter([...routes(), ...routes(":locale")]);

  return (
    <ApolloProvider client={apolloClient}>
      <RouterProvider router={router} />
      <ToastContainer limit={5} />
    </ApolloProvider>
  );
}
